import { useEffect, useRef } from "react";
import { colors, fonts } from "../../style";
import { Btn } from "./buttons";
import { ui } from "./styles";

// Centred dialog on a dimmed backdrop. Escape and backdrop clicks close it.
export function Dialog({ open, onClose, label, width = 440, top, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(10, 10, 18, 0.75)",
        display: "flex",
        justifyContent: "center",
        alignItems: top ? "flex-start" : "center",
        padding: top ? `${top} 16px 16px` : "16px",
        boxSizing: "border-box",
        overflowY: "auto",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={{
          width: "100%",
          maxWidth: width,
          boxSizing: "border-box",
          borderRadius: "16px",
          background: colors.dialog,
          border: `1px solid ${colors.borderStrong}`,
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.5)",
          fontFamily: fonts.system,
          color: colors.textStrong,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Slide-in panel chrome: backdrop, title, close button and Escape to close
export function SlideOver({
  label,
  onClose,
  children,
  closeLabel = "Close",
  width = 380,
}) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeRef.current();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 950,
          background: "rgba(10, 10, 18, 0.5)",
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 951,
          width: `min(${width}px, 100vw)`,
          boxSizing: "border-box",
          background: colors.sidebar,
          borderLeft: `1px solid ${colors.border}`,
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          overflowY: "auto",
          fontFamily: fonts.system,
          color: colors.textStrong,
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2
            style={{ margin: 0, fontSize: "18px", color: colors.text, flex: 1 }}
          >
            {label}
          </h2>
          <Btn
            variant="ghost"
            icon="close"
            aria-label={closeLabel}
            onClick={onClose}
          />
        </div>
        {children}
      </aside>
    </>
  );
}

// Dialog with a title, body, optional error and Cancel/Confirm buttons.
// label: the dialog's aria-label when it differs from the title.
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  error,
  confirmLabel,
  cancelLabel,
  loading,
  tone = "danger",
  label,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      label={label ?? (typeof title === "string" ? title : undefined)}
    >
      <div
        style={{
          padding: "22px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "18px", color: colors.text }}>
          {title}
        </h2>
        <p
          style={{ ...ui.muted, margin: 0, fontSize: "14px", lineHeight: 1.5 }}
        >
          {children}
        </p>
        {error && <p style={ui.error}>{error}</p>}
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          <Btn variant="ghost" onClick={onClose}>
            {cancelLabel}
          </Btn>
          <Btn variant={tone} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}
